import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiService } from "src/services";
import { useNavigate, useLocation } from "react-router-dom";
import { ICourseSummary, IEventSummary, IQuery, ICourse, IEvent, IClub } from "@eagerdog/interfaces";
import { getCourseCategoryLabel } from "@eagerdog/constants";
import moment from "moment";

import { Modal, useModal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import CalendarItem from "../CalendarItem/CalendarItem";
import Icon from "src/components/Icon/Icon";
import { toast } from "src/components/Toast/ToastManager";
import Congrats from "src/components/Congrats/Congrats";
import StripeOnboardingModal from "src/components/StripeOnboardingModal/StripeOnboardingModal";
import ClubCalendar from "src/components/ClubCalendar/ClubCalendar";

import EventForm from "src/components/StepForm/EventForm/EventForm";
import CreateCourse from "src/routes/Courses/CreateCourse/CreateCourse";
import EmbedModal from "src/components/EmbedModal/EmbedModal";

import styles from "./ScheduleTab.module.scss";

interface IProps {
  club: IClub,
  isAdmin: boolean,
  onEventCreate: () => void
}

const ScheduleTab: React.FC<IProps> = (props) => {
  const clubId = props.club._id.toString()
  const [fromMonth, setFromMonth] = useState(moment().startOf('month'));
  const [toMonth, setToMonth] = useState(moment().add(2, 'months').endOf('month'));
  const navigate = useNavigate();
  const location = useLocation();

  const { toggle, isShown } = useModal();
  const [modalContent, setModalContent] = useState<string>();
  const [newEventUrl, setNewEventUrl] = useState<string>("");

  const [calendarItems, setCalendarItems] = useState<any[]>([]);
  const [calendarView, setCalendarView] = useState<"list" | "calendar">("calendar");

  // ── Filter state ──────────────────────────────────────────────────────────
  const [showEvents, setShowEvents] = useState(true);
  const [showCourses, setShowCourses] = useState(true);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  // Maps show_type string → a unified display label (collapses related types)
  const SHOW_TYPE_LABEL: { [key: string]: string } = {
    "Agility":                    "Agility",
    "Barn Hunt":                  "Barn Hunt",
    "CAT":                        "Lure Coursing / Fast CAT",
    "Conformation":               "Conformation",
    "Dock Diving":                "Dock Diving",
    "Dock Jumping":               "Dock Diving",
    "Fast CAT":                   "Lure Coursing / Fast CAT",
    "Fast Dash":                  "Lure Coursing / Fast CAT",
    "Herding":                    "Herding",
    "Pre-Trial (Herding)":        "Herding",
    "Instinct Test (Herding)":    "Herding",
    "Lure Coursing":              "Lure Coursing / Fast CAT",
    "Nosework":                   "Nosework / Scentwork",
    "Scent":                      "Nosework / Scentwork",
    "Scentwork":                  "Nosework / Scentwork",
    "Sniff-n-Go":                 "Nosework / Scentwork",
    "Scent Dog Games":            "Nosework / Scentwork",
    "Obedience":                  "Obedience",
    "Rally Obedience":            "Rally Obedience",
    "Weight Pull":                "Weight Pull",
    "Urban Rat Race":             "Rat Race",
    "Country Rat Race":           "Rat Race",
    "Fetch":                      "Fetch",
    "Shed Dog":                   "Shed Dog",
    "Water Dog":                  "Water Dog",
    "Farm Dog":                   "Farm Dog",
    "Temperament Test (ATT)":     "ATT",
    "Canine Good Citizen":        "Canine Good Citizen",
    "Trick Dog":                  "Trick Dog",
    "Field Trial":                "Field Trial",
  };

  // Maps course_category value → unified display label (must match SHOW_TYPE_LABEL where applicable)
  const CATEGORY_LABEL: { [key: string]: string } = {
    "nosework":          "Nosework / Scentwork",
    "rally_obedience":   "Rally Obedience",
    "conformation":      "Conformation",
    "obedience":         "Obedience",
    "dock_diving":       "Dock Diving",
    "barn_hunt":         "Barn Hunt",
    "agility":           "Agility",
    "behaviour":         "Behaviour & Training",
    "tracking":          "Tracking",
    "herding":           "Herding",
    "lure_coursing":     "Lure Coursing / Fast CAT",
    "tricks":            "Trick Dog",
    "sport_foundations": "Sport Foundations",
    "handling":          "Handling & Presentation",
    "health_wellness":   "Health & Wellness",
    "other":             "Other",
  };

  const getItemUnifiedLabel = (item: any): string[] => {
    if (item.sanctioning_club) {
      // Event — collect from show_types
      const labels = (item.show_types ?? [])
        .map((st: any) => SHOW_TYPE_LABEL[st.show_type] ?? st.show_type)
        .filter(Boolean);
      return [...new Set<string>(labels)];
    } else {
      // Course
      if (!item.category) return [];
      return [CATEGORY_LABEL[item.category] ?? getCourseCategoryLabel(item.category)];
    }
  };

  // All unified labels — full static list from both maps, deduped and sorted
  const availableFilters = useMemo(() => {
    const labels = new Set<string>([
      ...Object.values(SHOW_TYPE_LABEL),
      ...Object.values(CATEGORY_LABEL),
    ]);
    return Array.from(labels).sort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFilter = (label: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  // Apply show/hide + tag filters to a grouped items object
  const filteredCalendarItems = useMemo(() => {
    const result: { [key: string]: any[] } = {};
    Object.entries(calendarItems).forEach(([monthKey, items]) => {
      const filtered = (items as any[]).filter(item => {
        const isEvent = !!item.sanctioning_club;
        if (isEvent && !showEvents) return false;
        if (!isEvent && !showCourses) return false;
        if (activeFilters.size === 0) return true;
        const labels = getItemUnifiedLabel(item);
        return labels.some(l => activeFilters.has(l));
      });
      if (filtered.length > 0) result[monthKey] = filtered;
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarItems, showEvents, showCourses, activeFilters]);

  const goToPreviousRange = () => {
    setFromMonth(prev => moment(prev).subtract(3, 'months'));
    setToMonth(prev => moment(prev).subtract(3, 'months'));
  };

  const goToNextRange = () => {
    setFromMonth(prev => moment(prev).add(3, 'months'));
    setToMonth(prev => moment(prev).add(3, 'months'));
  };

  // Handle new event creation redirect (show congratulations modal)
  const searchParams = new URLSearchParams(location.search);
  const newEvent = searchParams.get("e");

  useEffect(() => {
    if (newEvent !== null) {
      setNewEventUrl(newEvent);
      setModalContent("congratsModal");
      // Clear the query parameter from URL
      navigate(location.pathname, { replace: true });
    }
  }, [newEvent, navigate, location.pathname]);

  const getModalContent = () => {
      switch (modalContent) {
          case "congratsModal":
              return <Congrats 
                  text="You made an event! Now let's create some shows for your event." 
                  url={`/e/${newEventUrl}/?s=1&admin=${clubId}`}
                  urlText="Create Shows" 
              />;
          case "stripeOnboarding":
              return (
                  <StripeOnboardingModal
                      clubId={props.club._id}
                      onCancel={() => {
                          toggle();
                          setModalContent("");
                      }}
                  />
              );
          case "eventForm":
              return <EventForm
                  clubNumber={props.club?.club_number as string | undefined}
                  clubId={clubId}
                  onFinish={(e: IEvent) => {
                      toggle();
                      props.onEventCreate && props.onEventCreate()
                      navigate(`${location.pathname}?e=${e.handle}`, { replace: true });
                  }} 
              />;
          case "createCourse":
              return <CreateCourse
                  clubId={clubId}
                  onSubmit={(c: ICourse) => { 
                      toast.show({
                        title: "Create Course",
                        content: "Course Successfully Created",
                        duration: 5000,
                        type: "success"
                      });

                      navigate("/c/" + c.handle);
                      toggle(); 
                  }} 
              />;
          case "embedCalendar":
              return <EmbedModal
                  clubId={clubId}
                  clubName={props.club.club_name || ""}
              />;
      }

      return <>none</>;
  }

  const getEvents = useCallback(() => {
    let eventQuery:IQuery = {
      $limit: 200, 
      $skip: 0, 
      sort: [{ attribute_name: 'start_date', sort: "asc" }],
      query: {
        $and: [
          {
            attribute_name: "start_date",
            attribute_value: {
              operator: "$gte" as const,
              value: fromMonth.toISOString()
            }
          },
          {
            attribute_name: "start_date", 
            attribute_value: {
              operator: "$lte" as const,
              value: toMonth.toISOString()
            }
          }
        ]
      }
    };

    // Use protected endpoint for admins (includes draft events), public endpoint for regular users
    const eventsPromise = props.isAdmin 
      ? apiService.getClubEvents(clubId, eventQuery)
      : apiService.getPublicClubEvents(clubId, eventQuery);

    eventsPromise.then((clubEvents) => {
          let query:IQuery = {
            $limit: 200, 
            $skip: 0, 
            sort: [{ attribute_name: 'start_date', sort: "asc" }],
            query: {
              $and: [
                {
                  attribute_name: "start_date",
                  attribute_value: {
                    operator: "$gte" as const,
                    value: fromMonth.toISOString()
                  }
                },
                {
                  attribute_name: "start_date", 
                  attribute_value: {
                    operator: "$lte" as const,
                    value: toMonth.toISOString()
                  }
                },
                {
                  attribute_name: "club_id", 
                  attribute_value: {
                    operator: "$eq",
                    value: clubId
                  }
                }
              ]
            }
          };

          apiService.getFullnessEvents(clubEvents.map(e => e._id)).then((fullnessData) => {
            fullnessData.forEach(fd => {
              const event:any = clubEvents.find(e => e._id === fd._id);
              
              if (event && fd.fullness_shows.every(show => show.is_full)) {
                event.is_full = true;
              } else {
                event.is_full = false;
              }
            });

            const coursesPromise = props.isAdmin
              ? apiService.getPrivateCourses(query, clubId)
              : apiService.getCourses(query);

            coursesPromise.then((clubCourses) => {
              let _calendarItems:any = sortCalendarItems(clubEvents, clubCourses);
              setCalendarItems(_calendarItems);
            });
          });
    });
  }, [fromMonth, clubId, toMonth, props.isAdmin]);

  const sortCalendarItems = (events: IEventSummary[], courses: ICourseSummary[]) => {
    const sorted = [...events, ...courses].sort((a, b) => 
      moment(a.start_date).valueOf() - moment(b.start_date).valueOf()
    );

    const grouped: { [key: string]: any } = {};

    sorted.forEach(item => {
      const monthKey = moment(item.start_date).format('YYYY-MM');

      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }

      grouped[monthKey].push(item);
    });

    return grouped;
  }

  useEffect(() => {
    getEvents();
  }, [toMonth, getEvents]);

  return (
    <div className={styles.ScheduleTab}>
      {props.isAdmin && (
        <div className={styles.adminActions}>
          <div className={styles.adminLeft}>
          <Button className={styles.actionBtn} onClick={async () => { 
            if (props.club._id) {
              try {
                const status = await apiService.getAccountStatus(props.club._id);
                if (status.onboarding_status !== "requirements_met" || !status.charges_enabled) {
                  setModalContent("stripeOnboarding");
                  toggle();
                  return;
                }
              } catch (err) {
                console.error("Failed to fetch account status:", err);
              }
            }
            setModalContent("eventForm");
            toggle();
          }}>Create Event</Button>
          <Button className={styles.actionBtn} onClick={async () => { 
            if (props.club._id) {
              try {
                const status = await apiService.getAccountStatus(props.club._id);
                if (status.onboarding_status !== "requirements_met" || !status.charges_enabled) {
                  setModalContent("stripeOnboarding");
                  toggle();
                  return;
                }
              } catch (err) {
                console.error("Failed to fetch account status:", err);
              }
            }
            setModalContent("createCourse");
            toggle();
          }}>Create Course</Button>
          </div>
          <div className={styles.adminRight}>
            <button
              className={styles.embedBtn}
              onClick={() => { setModalContent("embedCalendar"); toggle(); }}
              title="Embed Calendar"
              type="button"
            >
              <Icon type="embed" className={styles.embedIcon} />
            </button>
          </div>
        </div>
      )}
      {/* Filter bar — always visible */}
      <div className={styles.actions}>
        <div className={styles.left}>
          <button
            className={`${styles.toggleBtn} ${styles.toggleBtnEvent} ${showEvents ? styles.toggleActive : ""}`}
            onClick={() => setShowEvents(v => !v)}
            type="button"
          >Events</button>
          <button
            className={`${styles.toggleBtn} ${styles.toggleBtnCourse} ${showCourses ? styles.toggleActive : ""}`}
            onClick={() => setShowCourses(v => !v)}
            type="button"
          >Courses</button>
          {availableFilters.length > 0 && <div className={styles.filterDivider} />}
          <div className={styles.filterScroll}>
            {availableFilters.map(label => (
              <button
                key={label}
                className={`${styles.filterTag} ${activeFilters.has(label) ? styles.filterTagActive : ""}`}
                onClick={() => toggleFilter(label)}
                type="button"
              >{label}</button>
            ))}
            {activeFilters.size > 0 && (
              <button
                className={styles.clearFilters}
                onClick={() => setActiveFilters(new Set())}
                type="button"
              >Clear filters</button>
            )}
          </div>
        </div>
        <div className={styles.right}>
          {/* Date range picker — list view only */}
          {calendarView === "list" && (
            <div className={styles.monthPickerWrap}>
              <div className={styles.monthPicker}>
                <button className={styles.navBtn} onClick={goToPreviousRange} type="button">
                  <Icon className={styles.small} type="caretLeft" />
                </button>
                <div className={styles.monthDisplay}>
                  <Icon className={styles.calendar} type="calendar" />
                  <span className={styles.monthText}>
                    {fromMonth.format("MMM YYYY")} - {toMonth.format("MMM YYYY")}
                  </span>
                </div>
                <button className={styles.navBtn} onClick={goToNextRange} type="button">
                  <Icon className={styles.small} type="caretRight" />
                </button>
              </div>
            </div>
          )}
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewToggleBtn} ${calendarView === "list" ? styles.viewToggleActive : ""}`}
              onClick={() => setCalendarView("list")}
              title="List view"
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="3" width="14" height="2" rx="1" fill="currentColor"/>
                <rect x="1" y="7" width="14" height="2" rx="1" fill="currentColor"/>
                <rect x="1" y="11" width="14" height="2" rx="1" fill="currentColor"/>
              </svg>
            </button>
            <button
              className={`${styles.viewToggleBtn} ${calendarView === "calendar" ? styles.viewToggleActive : ""}`}
              onClick={() => setCalendarView("calendar")}
              title="Calendar view"
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="2" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M1 6h14" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M5 1v2M11 1v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <rect x="3.5" y="8.5" width="2" height="2" rx="0.5" fill="currentColor"/>
                <rect x="7" y="8.5" width="2" height="2" rx="0.5" fill="currentColor"/>
                <rect x="10.5" y="8.5" width="2" height="2" rx="0.5" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {calendarView === "list" && Object.keys(calendarItems).length === 0 && <div className={styles.empty}>No Events/Courses Scheduled</div>}
      {calendarView === "list" && Object.keys(filteredCalendarItems).length === 0 && Object.keys(calendarItems).length > 0 && (
        <div className={styles.empty}>No results match your filters</div>
      )}
      {calendarView === "calendar" ? (
        <ClubCalendar clubId={clubId} isAdmin={props.isAdmin} showEvents={showEvents} showCourses={showCourses} activeFilters={activeFilters} />
      ) : (
        <>
          {Object.keys(filteredCalendarItems).sort().map((monthKey: any) => {
        const monthEvents = filteredCalendarItems[monthKey];
        const monthDate = moment(monthKey + '-01', 'YYYY-MM-DD');
        
        return (
          <div key={monthKey} className={styles.monthGroup}>
            <div className={styles.monthHeader}>
              <h2 className={styles.monthTitle}>{monthDate.format("MMMM YYYY")}</h2>
            </div>
            <div className={styles.monthEvents}>
              {monthEvents.map((item: any, index: number) => {
                return(
                  <CalendarItem
                    clubNumber={props.club?.club_number as string | undefined}
                    onEventCreate={props.onEventCreate}
                    isAdmin={props.isAdmin}
                    key={index}
                    type={item.sanctioning_club ? "event" : "course"}
                    item={item}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
        </>
      )}
      <Modal
          className={modalContent}
          isShown={isShown}
          hide={() => {
              toggle();
              setModalContent(undefined);
          }}
          modalContent={getModalContent()}
      />
    </div>
  );
};

export default ScheduleTab;
